import { ActivatedRoute } from "@angular/router";
import { ViewService } from "core/view.service";
import { LabelData } from "navigation/list/navigation-entry.interface";
import { MutexService } from "common/concurrency/mutex.service";
import { ViewableNavigationStore } from "navigation/list/viewable-navigation-store.interface";
import { EditService } from "process/element/edit/edit.service";
import { Injectable } from "@angular/core";

type ProcessTreeItem = stages.process.ProcessTreeItem;
type ViewableElement = stages.process.ViewableElement;
type ViewableType = stages.process.ViewableType;

@Injectable({ providedIn: "root" })
export class ProcessNavigationStore extends ViewableNavigationStore {
	constructor(private editService: EditService, private viewService: ViewService, private mutexService: MutexService) {
		super();
	}

	getTypeIconPrefix(): string {
		return "et";
	}

	getUppermostUpRoute(): unknown[] {
		return ["home"];
	}

	getRoute(entry: ProcessTreeItem): unknown[] {
		return ["process", entry.type.ident, entry.identity];
	}

	restoreRouteFromParams(params: ViewableElement): unknown[] {
		return ["process", params.type, params.identity];
	}

	getRouteForClipboardWorkspaceChange(activeEntry: ViewableElement): unknown[] {
		return ["process", activeEntry.type.ident, "index"];
	}

	getFilterForClipboardWorkspaceChange(
		activeEntry: ViewableElement,
	): { filterBy: string; filterParam1: string; filterParam2: string } | null {
		return {
			filterBy: "ELEMENT_TYPE",
			filterParam1: activeEntry.type.ident,
			filterParam2: "CREATE",
		};
	}

	isWorkspaceChangeSupportedForClipboard(): boolean {
		return true;
	}

	getPopupBaseRoute(): unknown[] {
		return ["process"];
	}

	getBaseRoute(activatedRoute: ActivatedRoute): ActivatedRoute {
		return activatedRoute.parent!.parent!.parent!;
	}

	getIdentifier(entry: ViewableElement): string {
		return entry.type.ident + entry.id;
	}

	getSelectionIdentifier(): string {
		return "process.navigation.selection";
	}

	getLabel(entry: ViewableElement): LabelData {
		return {
			label: entry.label,
			labelTranslate: undefined,
			labelClasses: entry.tailored ? "tailored" : "",
			secondLine: entry.type.name === null ? undefined : entry.type.name,
			secondLineTranslate: undefined,
			valueForAlphabeticalSort: entry.label,
		};
	}

	async rename(entry: ViewableElement, newLabel: string, workspaceId: string, pv: string): Promise<void> {
		return this.mutexService.invokeWithResult("rename", () =>
			this.editService.renameElement(entry, entry.type.ident, entry.identity, newLabel, workspaceId, pv),
		);
	}

	async addChild(
		parent: ViewableElement,
		type: ViewableType,
		newLabel: string,
		workspaceId: string,
		pv: string,
		skipDuplicateCheck: boolean = false,
	): Promise<ViewableElement> {
		return this.mutexService.invokeWithResult("add", () =>
			this.editService.addChild(parent, type, newLabel, workspaceId, pv, false, skipDuplicateCheck).then((child) => {
				this.viewService.notifyModified();
				return child;
			}),
		);
	}

	async deleteSingle(entry: ViewableElement, workspaceId: string, pv: string): Promise<void> {
		return this.mutexService.invokeWithResult(
			"delete",
			() =>
				new Promise<void>((resolve, reject) => {
					this.editService.deleteSingleElement(
						workspaceId,
						pv,
						entry,
						() => {
							resolve();
						},
						() => {
							reject("canceled");
						},
					);
				}),
		);
	}

	async rearrange(
		parent: ViewableElement,
		sortedEntries: ViewableElement[],
		sortOrder: "alphabetical" | "custom",
		workspaceId: string,
		pv: string,
	): Promise<ViewableElement[]> {
		return this.mutexService.invokeWithResult("rearrange", () =>
			this.editService
				.rearrange(workspaceId, pv, parent, sortOrder === "alphabetical", sortedEntries)
				.then(() => sortedEntries),
		);
	}

	async move(targetEntry: ViewableElement, entries: ViewableElement[], workspaceId: string, pv: string): Promise<void> {
		return this.mutexService.invokeWithResult("move", () =>
			this.editService.pasteElements(targetEntry, "move", entries, workspaceId, pv),
		);
	}

	async copy(targetEntry: ViewableElement, entries: ViewableElement[], workspaceId: string, pv: string): Promise<void> {
		return this.mutexService.invokeWithResult("copy", () =>
			this.editService.pasteElements(targetEntry, "copy", entries, workspaceId, pv),
		);
	}

	async delete(entries: ViewableElement[], workspaceId: string, pv: string): Promise<void> {
		return this.mutexService.invokeWithResult("delete", () =>
			this.editService.deleteElements(workspaceId, pv, entries),
		);
	}

	async executeCommand(
		commandType: "overwrite" | "restore",
		entry: ViewableElement,
		workspaceId: string,
		pv: string,
	): Promise<ViewableElement> {
		switch (commandType) {
			case "overwrite":
				return this.mutexService.invokeWithResult("overwrite", () =>
					this.editService.overwriteSingleElement(workspaceId, pv, entry).then(() => {
						overwriteDone(entry);
						return entry;
					}),
				);
			case "restore":
				return this.mutexService.invokeWithResult(
					"overwrite",
					() =>
						new Promise<ViewableElement>((resolve, reject) => {
							this.editService.restoreSingleElement(
								workspaceId,
								pv,
								entry,
								() => {
									restoreDone(entry);
									resolve(entry);
								},
								() => {
									reject("canceled");
								},
							);
						}),
				);
			default:
				throw new Error(`There is no implementation of ${commandType}`);
		}
	}

	async executeBatchCommand(
		commandType: "overwrite" | "restore",
		_targetEntry: ViewableElement,
		entries: ViewableElement[],
		workspaceId: string,
		pv: string,
	): Promise<void> {
		switch (commandType) {
			case "overwrite": {
				return this.mutexService.invokeWithResult("overwrite", () =>
					this.editService.overwriteElements(workspaceId, pv, entries).then(() => entries.forEach(overwriteDone)),
				);
			}
			case "restore": {
				return this.mutexService.invokeWithResult("overwrite", () =>
					this.editService.restoreElements(workspaceId, pv, entries).then(() => entries.forEach(restoreDone)),
				);
			}
			default: {
				throw new Error(`There is no implementation of ${commandType}`);
			}
		}
	}

	getTypeMessageKeySingular(type: ViewableType): string {
		return this.viewService.getTypeMessageKeySingular(type);
	}

	getIconClassesForType(type: ViewableType): string[] {
		return this.viewService.getIconClasses(type, false, false, "ico");
	}

	invalidate(): void {}
}

function overwriteDone(entry: ViewableElement): void {
	entry.allowedOperations.OVERWRITE = false;
	entry.allowedOperations.RESTORE = true;
}

function restoreDone(entry: ViewableElement): void {
	entry.allowedOperations.OVERWRITE = true;
	entry.allowedOperations.RESTORE = false;
}
